import type { ICandidateObjective } from "@bbj-types/challenge";
import { DropdownChip } from "@inputs/select/DropdownChip";
import type { ChipProps } from "@mui/material";
import { useAuthorizer } from "@navigations/Authorizer";
import type { Dispatch, SetStateAction } from "react";
import type { TPaperCandidateChallengeObjective } from "../types";
import { CandidateObjectiveLoadingState } from "./index";

const OBJECTIVE_STATUS_COLORS: Record<ICandidateObjective["status"], ChipProps["color"]> = {
  "in-progress": "primary",
  done: "success",
  missed: "missed",
  paused: "paused",
};

const OBJECTIVE_STATUS_LABELS: Record<ICandidateObjective["status"], string> = {
  "in-progress": "En cours",
  done: "Atteint",
  missed: "Abandonné",
  paused: "En pause",
};

const OBJECTIVE_STATUS_OPTIONS = Object.entries(OBJECTIVE_STATUS_LABELS).map(([value, label]) => ({
  value,
  label,
}));

export interface IPaperCandidateObjectiveStatusProps<T extends TPaperCandidateChallengeObjective> {
  candidateObjective: T;
  onEdit: (candidateObjective: T) => void | Promise<void>;
  loading: CandidateObjectiveLoadingState;
  setLoading: Dispatch<SetStateAction<CandidateObjectiveLoadingState>>;
}

export const PaperCandidateObjectiveStatus = <T extends TPaperCandidateChallengeObjective>({
  candidateObjective,
  onEdit,
  loading,
  setLoading,
}: IPaperCandidateObjectiveStatusProps<T>) => {
  const authorized = useAuthorizer(["candidate.challenges.modify"]);

  const handleChange = async (option: (typeof OBJECTIVE_STATUS_OPTIONS)[number]) => {
    setLoading(CandidateObjectiveLoadingState.EDIT_STATUS);
    try {
      await onEdit({ ...candidateObjective, status: option.value });
    } finally {
      setLoading(CandidateObjectiveLoadingState.NONE);
    }
  };

  if (candidateObjective.status === undefined) {
    return null;
  }
  return (
    <DropdownChip
      disabled={!authorized || loading !== CandidateObjectiveLoadingState.NONE}
      value={OBJECTIVE_STATUS_LABELS[candidateObjective.status]}
      options={OBJECTIVE_STATUS_OPTIONS}
      color={OBJECTIVE_STATUS_COLORS[candidateObjective.status]}
      onChange={handleChange}
    />
  );
};
